import { useCallback, useEffect, useRef } from "react";
import { FilterOption } from "../../../common/FilterChip";
import { ReportFacetOption } from "../../../../types/reportsV2";

/**
 * Filter-chip options from the latest run's facets. Facets only list values
 * present in the date range, so a selected value can drop out of them (e.g.
 * a staff member with no sales this week); it stays listed — with the label
 * remembered from an earlier run — so it can still be unchecked.
 */
export function useFacetOptions(
  facets: Record<string, ReportFacetOption[]> | undefined,
): (key: string, selected: string[]) => FilterOption[] {
  const labels = useRef(new Map<string, string>());

  useEffect(() => {
    Object.entries(facets ?? {}).forEach(([key, options]) => {
      options.forEach((o) =>
        labels.current.set(`${key}\u0000${o.value}`, o.label),
      );
    });
  }, [facets]);

  return useCallback(
    (key: string, selected: string[]) => {
      const options: FilterOption[] = [...(facets?.[key] ?? [])];
      const present = new Set(options.map((o) => o.value));
      selected
        .filter((value) => !present.has(value))
        .forEach((value) =>
          options.push({
            value,
            label: labels.current.get(`${key}\u0000${value}`) ?? value,
          }),
        );
      return options;
    },
    [facets],
  );
}
